import { useEffect, useState } from 'react';
import { auth } from '../services/firebase';

interface AdminDashboardProps {
    onLogout: () => void;
    onNavigateToSubscriptions: () => void;
    onNavigateToActivity: () => void;
    onNavigateToUsers: () => void;
    onNavigateToAdmins?: () => void;
}

const AdminDashboard = ({ onLogout, onNavigateToSubscriptions, onNavigateToActivity, onNavigateToUsers, onNavigateToAdmins }: AdminDashboardProps) => {
    const [adminEmail, setAdminEmail] = useState<string>('');

    useEffect(() => {
        // Get the current user's email from Firebase Auth
        const user = auth.currentUser;
        if (user?.email) {
            setAdminEmail(user.email);
        }
    }, []);

    const handleLogout = () => {
        console.log('Admin logout');
        onLogout();
    };

    return (
        <div className="min-h-screen bg-gray-50">
            <header className="bg-white shadow-sm border-b border-gray-200">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                            <div className="flex items-center space-x-2">
                                <span className="text-2xl">🛡️</span>
                                <h1 className="text-2xl font-bold text-gray-900">Admin Portal</h1>
                            </div>
                            <span className="px-3 py-1 bg-yellow-500 text-yellow-900 text-xs font-bold rounded-full">
                                👑 Super Admin
                            </span>
                        </div>
                        <div className="flex items-center space-x-4">
                            <span className="text-sm text-gray-600">{adminEmail}</span>
                            <button
                                onClick={handleLogout}
                                className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition"
                            >
                                Sign Out
                            </button>
                        </div>
                    </div>
                </div>
            </header>

            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="mb-8">
                    <h2 className="text-3xl font-bold text-gray-900 mb-2">
                        Welcome to the Admin Dashboard
                    </h2>
                    <p className="text-gray-600">
                        Manage users, subscriptions, and system settings.
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    <button
                        onClick={onNavigateToSubscriptions}
                        className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 hover:shadow-md hover:border-indigo-300 transition-all text-left group"
                    >
                        <div className="flex items-center space-x-3 mb-4">
                            <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center group-hover:bg-indigo-200 transition">
                                <span className="text-2xl">💳</span>
                            </div>
                            <h3 className="text-lg font-semibold text-gray-900">
                                Subscription Management
                            </h3>
                        </div>
                        <p className="text-sm text-gray-600 mb-3">
                            View and manage user subscriptions, billing, and payment history.
                        </p>
                        <div className="flex items-center text-indigo-600 text-sm font-medium">
                            <span>Open Subscription Dashboard</span>
                            <svg className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                        </div>
                    </button>

                    <button
                        onClick={onNavigateToActivity}
                        className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 hover:shadow-md hover:border-green-300 transition-all text-left group"
                    >
                        <div className="flex items-center space-x-3 mb-4">
                            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center group-hover:bg-green-200 transition">
                                <span className="text-2xl">📊</span>
                            </div>
                            <h3 className="text-lg font-semibold text-gray-900">
                                Activity Dashboard
                            </h3>
                        </div>
                        <p className="text-sm text-gray-600 mb-3">
                            View system analytics, admin actions, and audit logs.
                        </p>
                        <div className="flex items-center text-green-600 text-sm font-medium">
                            <span>Open Activity Dashboard</span>
                            <svg className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                        </div>
                    </button>

                    <button
                        onClick={onNavigateToUsers}
                        className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 hover:shadow-md hover:border-purple-300 transition-all text-left group"
                    >
                        <div className="flex items-center space-x-3 mb-4">
                            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center group-hover:bg-purple-200 transition">
                                <span className="text-2xl">👥</span>
                            </div>
                            <h3 className="text-lg font-semibold text-gray-900">
                                User Management
                            </h3>
                        </div>
                        <p className="text-sm text-gray-600 mb-3">
                            Browse, search, and manage all user accounts.
                        </p>
                        <div className="flex items-center text-purple-600 text-sm font-medium">
                            <span>Open User Management</span>
                            <svg className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                        </div>
                    </button>

                    {onNavigateToAdmins && (
                        <button
                            onClick={onNavigateToAdmins}
                            className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 hover:shadow-md hover:border-red-300 transition-all text-left group"
                        >
                            <div className="flex items-center space-x-3 mb-4">
                                <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center group-hover:bg-red-200 transition">
                                    <span className="text-2xl">🛡️</span>
                                </div>
                                <h3 className="text-lg font-semibold text-gray-900">
                                    Admin Team
                                </h3>
                            </div>
                            <p className="text-sm text-gray-600 mb-3">
                                Manage admin users, roles, and send invitations.
                            </p>
                            <div className="flex items-center text-red-600 text-sm font-medium">
                                <span>Manage Admin Team</span>
                                <svg className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                            </div>
                        </button>
                    )}
                </div>

                <div className="mt-8 grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
                        <p className="text-sm text-gray-600 mb-1">Total Users</p>
                        <p className="text-2xl font-bold text-gray-900">—</p>
                    </div>
                    <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
                        <p className="text-sm text-gray-600 mb-1">Active Subscriptions</p>
                        <p className="text-2xl font-bold text-gray-900">—</p>
                    </div>
                    <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
                        <p className="text-sm text-gray-600 mb-1">Monthly Revenue</p>
                        <p className="text-2xl font-bold text-gray-900">—</p>
                    </div>
                    <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
                        <p className="text-sm text-gray-600 mb-1">Support Tickets</p>
                        <p className="text-2xl font-bold text-gray-900">—</p>
                    </div>
                </div>
            </main>
        </div>
    );
};

export default AdminDashboard;